import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  DollarSign,
  Target,
  BarChart3,
  TrendingUp,
  LogOut,
  TrendingDown,
  Scale,
  CheckCircle,
} from "lucide-react";
import { getCurrentUser, logoutUser } from "../utils/auth";
import { getTransactions, getGoals } from "../utils/storage";
import Layout from "../components/Layout";
import BottomNavigation from "../components/BottomNavigation";

const Profile: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalGoals: 0,
    completedGoals: 0,
    totalIncome: 0,
    totalExpenses: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    // Calcular estatísticas do usuário
    const transactions = getTransactions(user.id);
    const goals = getGoals(user.id);

    const totalIncome = transactions
      .filter((t) => t.type === "income")
      .reduce((sum, t) => sum + t.amount, 0);

    const totalExpenses = transactions
      .filter((t) => t.type === "expense")
      .reduce((sum, t) => sum + t.amount, 0);

    setStats({
      totalTransactions: transactions.length,
      totalGoals: goals.length,
      completedGoals: goals.filter((g) => g.isCompleted).length,
      totalIncome,
      totalExpenses,
    });
  }, [user, navigate]);

  const handleLogout = () => {
    if (window.confirm("Tem certeza que deseja sair?")) {
      logoutUser();
      navigate("/login");
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("pt-BR");
  };

  if (!user) {
    return null;
  }

  return (
    <Layout
      title="Profile"
      subtitle="Manage your information and view your statistics"
    >
      {/* Main content */}
      {/* User information */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="mb-8"
      >
        <div className="card text-center">
          <div className="w-24 h-24 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl text-white font-bold">
              {user.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{user.name}</h2>
          <p className="text-gray-400 mb-4">{user.email}</p>
          <p className="text-sm text-gray-500">
            Member since {formatDate(user.createdAt)}
          </p>
        </div>
      </motion.div>

      {/* Statistics */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="mb-8"
      >
        <h3 className="text-lg font-semibold text-white mb-4">
          Suas Estatísticas
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center mb-2">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <p className="text-2xl font-bold text-white">
              {stats.totalTransactions}
            </p>
            <p className="text-sm text-gray-400">Transactions</p>
          </div>

          <div className="card text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-purple-600 rounded-xl flex items-center justify-center mb-2">
              <Target className="w-6 h-6 text-white" />
            </div>
            <p className="text-2xl font-bold text-white">{stats.totalGoals}</p>
            <p className="text-sm text-gray-400">Goals</p>
          </div>

          <div className="card text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-green-600 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <p className="text-2xl font-bold text-white">
              {stats.completedGoals}
            </p>
            <p className="text-sm text-gray-400">Completed</p>
          </div>

          <div className="card text-center">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mb-2">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <p className="text-2xl font-bold text-white">
              {(
                (stats.completedGoals / Math.max(stats.totalGoals, 1)) *
                100
              ).toFixed(0)}
              %
            </p>
            <p className="text-sm text-gray-400">Success Rate</p>
          </div>
        </div>
      </motion.div>

      {/* Financial summary */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="mb-8"
      >
        <h3 className="text-lg font-semibold text-white mb-4">
          Financial Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Income</p>
                <p className="text-2xl font-bold text-green-400">
                  {formatCurrency(stats.totalIncome)}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-green-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Expenses</p>
                <p className="text-2xl font-bold text-red-400">
                  {formatCurrency(stats.totalExpenses)}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-red-400 to-red-600 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="card mt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Balance</p>
              <p
                className={`text-2xl font-bold ${
                  stats.totalIncome - stats.totalExpenses >= 0
                    ? "text-green-400"
                    : "text-red-400"
                }`}
              >
                {formatCurrency(stats.totalIncome - stats.totalExpenses)}
              </p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
              <Scale className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Settings */}
      {/* <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mb-8"
      >
        <h3 className="text-lg font-semibold text-white mb-4">Settings</h3>
        <div className="space-y-3">
          <button className="w-full p-4 bg-dark-800 hover:bg-dark-700 rounded-lg flex items-center justify-between transition-colors">
            <div className="flex items-center space-x-3">
              <Bell className="w-6 h-6 text-primary-400" />
              <div className="text-left">
                <p className="text-white font-medium">Notifications</p>
                <p className="text-gray-400 text-sm">
                  Manage reminders and alerts
                </p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </button>

          <button className="w-full p-4 bg-dark-800 hover:bg-dark-700 rounded-lg flex items-center justify-between transition-colors">
            <div className="flex items-center space-x-3">
              <Palette className="w-6 h-6 text-primary-400" />
              <div className="text-left">
                <p className="text-white font-medium">Theme</p>
                <p className="text-gray-400 text-sm">Customize appearance</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </button>

          <button className="w-full p-4 bg-dark-800 hover:bg-dark-700 rounded-lg flex items-center justify-between transition-colors">
            <div className="flex items-center space-x-3">
              <Download className="w-6 h-6 text-primary-400" />
              <div className="text-left">
                <p className="text-white font-medium">Backup</p>
                <p className="text-gray-400 text-sm">Export your data</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </button>

          <button className="w-full p-4 bg-dark-800 hover:bg-dark-700 rounded-lg flex items-center justify-between transition-colors">
            <div className="flex items-center space-x-3">
              <HelpCircle className="w-6 h-6 text-primary-400" />
              <div className="text-left">
                <p className="text-white font-medium">Help</p>
                <p className="text-gray-400 text-sm">Support center</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </motion.div> */}

      {/* About the app */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="mb-8"
      >
        <div className="card bg-gradient-to-r from-primary-600/20 to-blue-600/20 border-primary-500/30">
          <div className="flex items-center space-x-4 mb-4">
            <img src="/logo.png" alt="Green Tracker" className="w-12" />
            <div>
              <h4 className="text-lg font-semibold text-white">
                Green Tracker
              </h4>
              <p className="text-gray-400 text-sm">Version 1.0.0</p>
            </div>
          </div>
          <p className="text-gray-300 text-sm leading-relaxed">
            Green Tracker is a financial education app developed to help you
            control your finances, set goals and learn about investments. Its
            goal is to promote education financial and personal planning.
          </p>
        </div>
      </motion.div>

      {/* Logout button */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleLogout}
          className="w-full py-4 bg-red-600 hover:bg-red-700 text-white font-semibold
           rounded-lg transition-colors flex items-center justify-center"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Sign Out
        </motion.button>
      </motion.div>

      <BottomNavigation />
    </Layout>
  );
};

export default Profile;
